import { CheckboxFieldGroupToReference, FieldToReference } from '@modusign/react-signing/types';
import { produce } from 'immer';

import { hasRequired } from '@app/features/apply-react-signing/libs/fieldTypeGuards';
import { pureSortFields } from '@app/features/apply-react-signing/libs/sortFields';
import {
  CONTRACT_PROGRESS_CONTRACT_LOAD_REQUEST,
  CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS,
  CONTRACT_PROGRESS_SEQUENCE_INSERT,
  CONTRACT_PROGRESS_SEQUENCE_REMOVE,
} from '@app/store/actionTypes';
import { ContractProgressAction } from '@app/store/contract-progress/actions';

export type FieldNodeForSequence =
  | Pick<FieldToReference, 'id' | 'type'>
  | (Pick<CheckboxFieldGroupToReference, 'id' | 'type'> & {
      children: FieldNodeForSequence[];
    });

type ContractProgressTrackerSequenceState = FieldNodeForSequence[];

const initialState: ContractProgressTrackerSequenceState = [];

/**
 * 트래커는 필수 입력인 필드를 대상으로 추적합니다.
 * 필드 위치를 기준으로 정렬한 후 필수 입력 필드의 순서를 저장합니다.
 * 시퀀스는 저장하고 변경이 없습니다.
 */

function fieldMapper(
  field: FieldToReference | CheckboxFieldGroupToReference
): FieldNodeForSequence {
  if (field.type === 'CHECKBOX_GROUP') {
    return {
      id: field.id,
      type: field.type,
      children: field.children
        .filter(
          (field): field is FieldToReference | CheckboxFieldGroupToReference =>
            field.type !== 'AUTOFILL_GROUP'
        )
        .map(fieldMapper),
    };
  }
  return {
    type: field.type,
    id: field.id,
  };
}

export default function reducer(
  state: ContractProgressTrackerSequenceState = initialState,
  action: ContractProgressAction
): ContractProgressTrackerSequenceState {
  switch (action.type) {
    case CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS: {
      const { fields } = action.payload;

      const flatted = pureSortFields(
        fields.flatMap((f) => {
          if (f.type === 'AUTOFILL_GROUP') {
            return f.children;
          }
          return f;
        })
      );

      return flatted
        .filter((field) => hasRequired(field.config) && field.config.required)
        .filter(
          (field): field is FieldToReference | CheckboxFieldGroupToReference =>
            field.type !== 'AUTOFILL_GROUP'
        )
        .map<FieldNodeForSequence>(fieldMapper);
    }
    case CONTRACT_PROGRESS_SEQUENCE_INSERT: {
      return produce(state, (draft) => {
        const node = action.payload.fieldNode;
        if (draft.length === 0) {
          draft.push({
            id: node.id,
            type: node.type,
          } as FieldNodeForSequence);
        }

        if (state.findIndex(({ id }) => node.id === id) >= 0) {
          return;
        }

        draft.splice(0, 0, {
          id: node.id,
          type: node.type,
        } as FieldNodeForSequence);
      });
    }

    case CONTRACT_PROGRESS_SEQUENCE_REMOVE: {
      return produce(state, (draft) => {
        const targetIndex = draft.findIndex(({ id }) => action.payload.id === id);
        if (targetIndex >= 0) {
          draft.splice(targetIndex, 1);
        }
      });
    }
    case CONTRACT_PROGRESS_CONTRACT_LOAD_REQUEST:
      return initialState;

    default:
      return state;
  }
}
